import moment from 'moment-jalaali';

const persianToEnglishDigits = (str) => {
  if (str == null) return str;
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  const englishDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  let result = String(str);
  persianDigits.forEach((persian, index) => {
    result = result.replace(new RegExp(persian, 'g'), englishDigits[index]);
  });
  return result;
};

/** تبدیل هر مقدار تاریخ فیلتر به رشته جلالی YYYY/MM/DD با ارقام لاتین */
export const normalizeJalaliFilterDate = (value) => {
  if (value == null || value === '') return value;

  if (typeof value === 'number' || (typeof value === 'string' && /^\d{10,13}$/.test(String(value).trim()))) {
    const ms = typeof value === 'number' ? value : Number(String(value).trim());
    return persianToEnglishDigits(moment(ms).format('jYYYY/jMM/jDD'));
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{10,13}$/.test(trimmed)) {
      return persianToEnglishDigits(moment(Number(trimmed)).format('jYYYY/jMM/jDD'));
    }
    return persianToEnglishDigits(trimmed.replace(/-/g, '/'));
  }

  if (value instanceof Date) {
    return persianToEnglishDigits(moment(value).format('jYYYY/jMM/jDD'));
  }

  if (moment.isMoment(value)) {
    return persianToEnglishDigits(value.format('jYYYY/jMM/jDD'));
  }

  // react-multi-date-picker / react-date-object (تقویم جلالی)
  if (value?.year != null && value?.month != null && value?.day != null) {
    const monthNum = value.month?.number ?? value.month;
    const y = value.year;
    const m = String(monthNum).padStart(2, '0');
    const d = String(value.day).padStart(2, '0');
    return persianToEnglishDigits(`${y}/${m}/${d}`);
  }

  if (value?.format && typeof value.format === 'function') {
    try {
      const jalaliFormatted = value.format('YYYY/MM/DD');
      if (jalaliFormatted && !/invalid/i.test(String(jalaliFormatted))) {
        return persianToEnglishDigits(jalaliFormatted);
      }
    } catch {
      // fall through
    }
    try {
      const formatted = value.format('jYYYY/jMM/jDD');
      if (formatted && !/invalid/i.test(String(formatted))) {
        return persianToEnglishDigits(formatted);
      }
    } catch {
      // ignore
    }
  }

  return value;
};

/** برای query string API — جداکننده خط تیره */
export const formatJalaliDateForApi = (value) => {
  const normalized = normalizeJalaliFilterDate(value);
  if (!normalized || typeof normalized !== 'string') return normalized;
  return persianToEnglishDigits(normalized.replace(/\//g, '-'));
};

export const normalizeListFilters = (filters = {}) => {
  if (!filters || typeof filters !== 'object') return filters;
  const next = { ...filters };
  if (next.startDate != null) next.startDate = normalizeJalaliFilterDate(next.startDate);
  if (next.endDate != null) next.endDate = normalizeJalaliFilterDate(next.endDate);
  return next;
};

/**
 * آیدی فیلتر Select را به number تبدیل می‌کند تا بعد از بازگشت از URL
 * (که همیشه string است) با option.value عددی match شود و به‌جای نام، آیدی نشان داده نشود.
 * مقادیر 'all' / خالی دست‌نخورده می‌مانند.
 */
export const toFilterSelectId = (value, fallback = 'all') => {
  if (value == null || value === '' || value === 'all') return 'all';
  const n = Number(value);
  return Number.isNaN(n) ? fallback : n;
};

/** گزینه‌های Select فیلتر — value همیشه number تا با state بعد از restore match شود */
export const mapFilterSelectOptions = (items, { valueKey = 'id', labelKey = 'name', getLabel } = {}) =>
  (items || [])
    .map((item) => {
      const value = toFilterSelectId(item?.[valueKey]);
      if (value === 'all') return null;
      const label = getLabel ? getLabel(item) : item?.[labelKey];
      return label != null && String(label).trim() !== ''
        ? { value, label: String(label) }
        : null;
    })
    .filter(Boolean);

/** اگر مقدار انتخاب‌شده در options نیست، با label ذخیره‌شده نمایش داده شود */
export const ensureFilterSelectOption = (options, selectedValue, fallbackLabel) => {
  if (selectedValue == null || selectedValue === '' || selectedValue === 'all') return options;
  const id = toFilterSelectId(selectedValue);
  if (id === 'all') return options;
  if ((options || []).some((o) => Number(o.value) === Number(id))) return options;
  if (!fallbackLabel) return options;
  return [{ value: id, label: fallbackLabel }, ...(options || [])];
};

/** لیست آیدی‌ها (مثل tags) — آرایهٔ عدد یا 'all' */
export const toFilterSelectIdList = (value) => {
  if (value == null || value === '' || value === 'all') return 'all';
  const raw = Array.isArray(value)
    ? value
    : String(value)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
  const ids = raw.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
  return ids.length > 0 ? ids : 'all';
};

/** تبدیل مقدار RangePicker به startDate/endDate */
export const parseJalaliDateRangeValue = (value) => {
  const toStr = (item) => normalizeJalaliFilterDate(item) || null;
  if (!value) return { startDate: null, endDate: null };
  if (Array.isArray(value)) {
    return { startDate: toStr(value[0]), endDate: toStr(value[1]) };
  }
  if (typeof value === 'object' && (value.start || value.end)) {
    return { startDate: toStr(value.start), endDate: toStr(value.end) };
  }
  return { startDate: null, endDate: null };
};

/** بازه‌های سریع مشترک فیلتر تاریخ */
export const buildCommonDatePresets = () => {
  const format = (date) => persianToEnglishDigits(date.format('jYYYY/jMM/jDD'));
  return [
    { value: 'today', label: 'روز جاری', range: { startDate: format(moment()), endDate: format(moment()) } },
    { value: 'tomorrow', label: 'روز آینده', range: { startDate: format(moment().add(1, 'day')), endDate: format(moment().add(1, 'day')) } },
    { value: 'yesterday', label: 'روز گذشته', range: { startDate: format(moment().subtract(1, 'day')), endDate: format(moment().subtract(1, 'day')) } },
    { value: 'last_week', label: 'هفته گذشته', range: { startDate: format(moment().subtract(7, 'day')), endDate: format(moment()) } },
    { value: 'current_month', label: 'ماه جاری', range: { startDate: format(moment().startOf('jMonth')), endDate: format(moment().endOf('jMonth')) } },
    { value: 'last_month', label: 'ماه گذشته', range: { startDate: format(moment().subtract(1, 'jMonth').startOf('jMonth')), endDate: format(moment().subtract(1, 'jMonth').endOf('jMonth')) } },
    { value: 'last_3_months', label: 'سه ماه گذشته', range: { startDate: format(moment().subtract(3, 'months')), endDate: format(moment()) } },
    { value: 'last_year', label: 'یکسال گذشته', range: { startDate: format(moment().subtract(1, 'year')), endDate: format(moment()) } },
  ];
};
